import axios from 'axios';
import config from '../../../config';
import MsalConfig, { loginRequest  } from '../EntraUtils/AuthConfig';
import { parseJwt } from '../commonUtil';
const axiosInstance = axios.create();

// Track 403 errors to prevent redirect loops
let last403Time = 0;
const REDIRECT_COOLDOWN = 5000; // 5 seconds cooldown between redirects

// Response interceptor to handle 401 and 403 errors
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;

    if (status === 401 || status === 403) {
      const now = Date.now();
      const timeSinceLastRedirect = now - last403Time;

      // Check if we're in a cooldown period to prevent rapid redirects
      if (timeSinceLastRedirect < REDIRECT_COOLDOWN) {
        console.warn(`[httpUtil] >> ${status} occurred but in cooldown period (${Math.floor(timeSinceLastRedirect/1000)}s since last redirect). Not redirecting.`);
        return Promise.reject(error);
      }

      // Check if we're on certain paths where redirect should be avoided
      const currentPath = window.location.pathname;
      const noRedirectPaths = ['/login', '/', '/market-segments'];

      if (noRedirectPaths.includes(currentPath)) {
        console.warn(`[httpUtil] >> ${status} on ${currentPath}, not redirecting to avoid loop`);
        return Promise.reject(error);
      }

      // 403 means authenticated but not authorized - logout the user
      if (status === 403) {
        console.error(`[httpUtil] >> Forbidden (403). Logging out user.`);
        last403Time = now;

        localStorage.clear();
        sessionStorage.clear();

        // Clear MSAL cache and logout
        const msalConfig = new MsalConfig();
        const msalInstance = msalConfig.getMsalInstance();

        msalInstance.clearCache().then(() => {
          msalInstance.logoutRedirect();
        }).catch(() => {
          msalInstance.logoutRedirect();
        });

        return Promise.reject(error);
      }

      // 401 means not authenticated - need to re-authenticate
      if (status === 401) {
        console.error(`[httpUtil] >> Unauthorized (401). Clearing session and redirecting.`);
        last403Time = now;

        localStorage.clear();
        sessionStorage.clear();

        // Clear MSAL cache and redirect
        const msalConfig = new MsalConfig();
        const msalInstance = msalConfig.getMsalInstance();

        msalInstance.clearCache().then(() => {
          window.location.href = window.location.origin + '/login';
        }).catch(() => {
          window.location.href = window.location.origin + '/login';
        });
      }
    }
    return Promise.reject(error);
  }
);
const {currentRegion, currentEnv, currentName} = config;
const CURRENTENV = currentEnv.toLowerCase();
const {apiConfig, oidc} = config[currentRegion];
const BASE_URL = apiConfig[CURRENTENV].baseURL;
const BASE_CUSTOM_URL = apiConfig[CURRENTENV].baseCustomUGURL;

export const getCurrentRegionOIDCConfigs = () => oidc[CURRENTENV];
export const getCurrentRegionAPIConfigs = () => apiConfig[CURRENTENV];

export default function clearAllCookies(names) {
  if (window) {
    names.forEach((name) => {
      document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
    });
  }
}

export const _apiHeaderConfigs = async () => {
  const msalConfig = new MsalConfig();
  const msalInstance = msalConfig.getMsalInstance();
  const accounts = msalInstance.getAllAccounts();
  // Ensure we have an account to acquire token for
  if (accounts.length > 0) {
      const account = accounts[0];
      try {
          const response = await msalInstance.acquireTokenSilent({
              ...loginRequest,
              account: account
          });
          const idTokenData = parseJwt(response.idToken);
          return {
              _accessToken: response.idToken,
              _idToken: response.idToken,
              idTokenData,
          };
      } catch (error) {
          console.error("Token acquisition failed", error);
          // If silent acquisition fails, we don't force a redirect here to avoid loops in tab scenarios
          return {
              _accessToken: false,
              _idToken: false, 
          };
      }
  } else {
    console.log("DEBUG _apiHeaderConfigs else: No accounts found.");
    return {
      _accessToken: false,
      _idToken: false,
    };
  }
};

export const makeRequest = async (params) => {
  const {method} = params;
  let resp = {};
  switch (method) {
    case 'GET':
      resp = await makeGetRequest(params);
      return resp;
    case 'POST':
      resp = await makePostRequest(params);
      return resp;
    case 'PUT':
      resp = await makePutRequest(params);
      return resp;
    case 'DELETE':
      resp = await makeDeleteRequest(params);
      return resp;
    case 'PATCH':
      resp = await makePatchRequest(params);
      return resp;
    default:
      console.warn('[httpUtil] >> [makeRequest] >> Please provide valid HTTP method.');
  }
};

const makeGetRequest = async (params) => {
  const {endpoint, url} = params;
  const _headers = await _apiHeaderConfigs();
  const resp = await axiosInstance
    .get(url ? url : BASE_URL + endpoint, {
      headers: {
        'Content-Type': 'application/json',
        authorization: _headers._accessToken,
        profile: _headers._idToken,
      },
    })
    .then((response) => {
      const {url} = params;
      if (url) {
        return response;
      } else {
        const {status} = response;
        if (status === 200) {
          return response && response.data ? response.data : [];
        } else if (status === 500 || status >= 500) {
          return 'ERR_NETWORK';
        }
      }
    })
    .catch((err) => {
      return err.code && 'ERR_NETWORK';
    });
  return resp;
};

const makePostRequest = async (params) => {
  const {endpoint, body} = params;
  const _headers = await _apiHeaderConfigs();
  const isFormData = body instanceof FormData;

  const resp = await axiosInstance
    .post(BASE_URL + endpoint, body, {
      headers: {
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        authorization: _headers._accessToken,
        profile: _headers._idToken,
      },
    })
    .then((response) => {
      const {status} = response;
      if (status === 200 || status === 201) {
        return response && response.data ? response.data : [];
      } else if (status === 500 || status >= 500) {
        return 'ERR_NETWORK';
      }
    })
    .catch((err) => {
      return err.code;
    });

  return resp;
};

// POST Request V2
export const makePostRequest_V2 = async (params) => {
  const { endpoint, body } = params;
  const _headers = await _apiHeaderConfigs();
  const isFormData = body instanceof FormData;
  try {
    const response = await axiosInstance.post(BASE_URL + endpoint, body, {
      headers: {
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        authorization: _headers._accessToken,
        profile: _headers._idToken,
      },
    });
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
};

const makePutRequest = async (params) => {
  const {endpoint, body} = params;
  const _headers = await _apiHeaderConfigs();
  const isFormData = body instanceof FormData;
  const resp = await axiosInstance
    .put(BASE_URL + endpoint, body, {
      headers: {
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        authorization: _headers._accessToken,
        profile: _headers._idToken,
      },
    })
    .then((response) => {
      const {status} = response;
      if (status === 200 || status === 201) {
        return response && response.data ? response.data : [];
      } else if (status === 500 || status >= 500) {
        return 'ERR_NETWORK';
      }
    })
    .catch((err) => {
      return err.code;
    });
  return resp;
};

const makePatchRequest = async (params) => {
  const {endpoint, body} = params;
  const _headers = await _apiHeaderConfigs();
  const isFormData = body instanceof FormData;
  const resp = await axiosInstance
    .patch(BASE_URL + endpoint, body, {
      headers: {
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        authorization: _headers._accessToken,
        profile: _headers._idToken,
      },
    })
    .then((response) => {
      const {status} = response;
      if (status === 200 || status === 201) {
        return response && response.data ? response.data : [];
      } else if (status === 500 || status >= 500) {
        return 'ERR_NETWORK';
      }
    })
    .catch((err) => {
      return err.code;
    });
  return resp;
};

const makeDeleteRequest = async (params) => {
  const {endpoint, body} = params;
  const _headers = await _apiHeaderConfigs();
  if (body?.audienceGroups?.deleteAudGrpIds?.length > 0) {
    const resp = await axiosInstance
      .delete(BASE_URL + endpoint, {
        headers: {
          'Content-Type': 'application/json',
          authorization: _headers._accessToken,
          profile: _headers._idToken,
        },
        data: body,
      })
      .then((response) => {
        const {status} = response;
        if (status === 200 || status === 201) {
          return response && response.data ? response.data : [];
        }
      })
      .catch((err) => {
        return err.code;
      });
    return resp;
  } else {
    const resp = await axiosInstance
      .delete(BASE_URL + endpoint, {
        headers: {
          'Content-Type': 'application/json',
          authorization: _headers._accessToken,
          profile: _headers._idToken,
        },
        data: body,
      })
      .then((response) => {
        const {status} = response;
        if (status === 200 || status === 201) {
          return response && response.data ? response.data : [];
        }
      })
      .catch((err) => {
        return err.code;
      });
    return resp;
  }
};

// Helper to handle errors
const handleError = (error) => {
  if (error.response) {
    // The request was made and the server responded with a status code
    const message = error.response.data?.message || error.response.statusText || 'An unexpected error occurred.';
    console.error(`[httpUtil] >> Server error: ${message}`);
    return {
      error: 'ERR_SERVER',
      success: false,
      message: message,
      status : error.response?.status,
    };
  } else if (error.request) {
    // The request was made but no response was received
    console.error('[httpUtil] >> No response received from the server.');
    return {
      error: 'ERR_NETWORK',
      success: false,
      message: 'No response received from the server.',
    };
  } else {
    // Something happened in setting up the request that triggered an Error
    console.error(`[httpUtil] >> Error in request setup: ${error.message}`);
    return {
      error: 'ERR_UNKNOWN',
      success: false,
      message: `Error in request setup: ${error.message}`,
    };
  }
};

export const makeCustomUGRequest = async (params) => {
  const {
    endpoint,
    method,
    body = {},
    contentType = 'application/json',
    customHeaders = {},
  } = params;
  const _headers = await _apiHeaderConfigs();
  const headers = {
    'Content-Type': contentType,
    authorization: `Bearer ${_headers._accessToken}`,
    ...customHeaders,
  };
  try {
    const response = await axiosInstance[method](BASE_CUSTOM_URL + endpoint, ...(method==='post' || method === 'put' ? [body, {
      headers,
    }] : [{headers}]))
    return response;
  } catch (error) {
    return 'ERR_NETWORK';
  }
};

// Helper to handle successful responses
const handleResponse = (response) => {
  const { status } = response;
  if (status >= 200 && status < 300) {
    return { success: true, data: response?.data || [] }
  } else {
    return { error: 'ERR_NETWORK', success: false, message: `Unexpected status code: ${status}` };
  }
};
